import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal, Button, Table, Form } from "react-bootstrap";
import { roundMoneyUAH, roundPurchaseItemQty } from "../helpers/purchaseMoney";
import { purchaseUsesCashlessMarkup } from "../helpers/purchasePaymentType";
import { MATERIAL_CATEGORY_OPTIONS, MATERIAL_COLOR_NA_PLACEHOLDER, isPrintsEmbroideryCategory } from "../helpers/materialCategories";
import { getWindingMetersFromPropertyPairs } from "../helpers/materialWindingMeters";
import {
    effectiveStockUnitPriceUAH,
    effectiveStockUnitPriceUSD,
} from "../lib/materialStockUnitPriceUAH";

function formatUkMoneyHint2(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) {
        return "—";
    }
    return n.toLocaleString("uk-UA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

/** Під полями ₴/$: остання закупівля або орієнтир з картки (без закупівлі) — як підказка до введення. */
function PurchaseRowPriceHint({ material }) {
    if (!material) {
        return null;
    }
    if (material.lastPurchaseDate) {
        const uah = Number(material.lastPurchaseUnitPriceUAH);
        const usd = Number(material.lastPurchaseUnitPriceUSD);
        const bits = [];
        if (Number.isFinite(uah) && uah > 0) {
            bits.push(`${formatUkMoneyHint2(uah)} ₴`);
        }
        if (Number.isFinite(usd) && usd > 0) {
            bits.push(`${formatUkMoneyHint2(usd)} $`);
        }
        const prices = bits.length ? `${bits.join(" · ")}/од.` : null;
        const d = new Date(material.lastPurchaseDate).toLocaleDateString("uk-UA");
        return (
            <div className="small text-muted mt-1 lh-sm">
                <span className="fw-medium">Остання закупівля</span> ({d})
                {prices ? `: ${prices}` : ""}
            </div>
        );
    }
    const uEff = effectiveStockUnitPriceUAH(material);
    const usdEff = effectiveStockUnitPriceUSD(material);
    const bits = [];
    if (uEff != null && Number.isFinite(uEff) && uEff > 0) {
        bits.push(`${formatUkMoneyHint2(uEff)} ₴`);
    }
    if (usdEff != null && Number.isFinite(usdEff) && usdEff > 0) {
        bits.push(`${formatUkMoneyHint2(usdEff)} $`);
    }
    if (!bits.length) {
        return null;
    }
    return (
        <div className="small text-muted mt-1 lh-sm">
            <span className="fw-medium">Орієнтир із картки</span> (немає закупівлі): {bits.join(" · ")}/од.
        </div>
    );
}

function findMaterialForPurchaseRow(item, materialsList) {
    if (!item?.typeId || !item?.color) {
        return null;
    }
    return (
        materialsList.find(
            (m) => String(m.typeId?._id || m.typeId) === String(item.typeId) && m.color === item.color
        ) || null
    );
}

function unitLooksLikeMeters(unit) {
    const u = String(unit ?? "")
        .trim()
        .toLowerCase();
    return u === "м" || u === "m";
}

function unitLooksLikePiecesPurchase(unit) {
    const u = String(unit ?? "")
        .trim()
        .toLowerCase();
    return (
        u === "шт" ||
        u === "шт." ||
        u === "pcs" ||
        u === "pc" ||
        u === "штук" ||
        u === "штука"
    );
}

/** Матеріал з «Намоткою», склад у метрах — у рядку кількість трактуємо як бобіни (і при од. «шт»). */
function materialRowPurchaseIsBobbins(item, category, typeRow, materialsList) {
    if (category === "FABRIC") {
        return false;
    }
    const mat = findMaterialForPurchaseRow(item, materialsList);
    const w = mat?.propertyPairs ? getWindingMetersFromPropertyPairs(mat.propertyPairs) : null;
    if (w == null || !(w > 0)) {
        return false;
    }
    const warehouseUnit = String(mat?.unit ?? "").trim().toLowerCase();
    if (warehouseUnit !== "м" && warehouseUnit !== "m") {
        return false;
    }
    const purchaseUnit = String(item.unit ?? "").trim().toLowerCase();
    if (unitLooksLikeMeters(purchaseUnit)) {
        return true;
    }
    if (unitLooksLikePiecesPurchase(purchaseUnit)) {
        return true;
    }
    if (
        typeRow?.units?.length === 1 &&
        unitLooksLikeMeters(String(typeRow.units[0] || ""))
    ) {
        return true;
    }
    return false;
}

export default function CreatePurchaseModal({ show, onHide, reload, editId }) {

    const [suppliers,setSuppliers] = useState([]);
    const [materialTypes,setMaterialTypes] = useState([]);
    const [materials,setMaterials] = useState([]);
    const [category,setCategory] = useState("FABRIC");
    const [date,setDate] = useState(new Date().toISOString().slice(0,10));
    const [paymentType, setPaymentType] = useState("cash");
    const [cashlessPercent, setCashlessPercent] = useState(0); // %


    const [supplierId,setSupplierId] = useState("");

    const [workshops, setWorkshops] = useState([]);
    /** Порожньо — сервер зарахує на склад нашого цеху. */
    const [destinationWorkshopId, setDestinationWorkshopId] = useState("");

    const [currency,setCurrency] = useState("USD");
    const [exchangeRate,setExchangeRate] = useState(39);

    const [items,setItems] = useState([]);

    useEffect(()=>{

        loadData();
        loadRates();

    },[]);

    useEffect(()=>{

        if(show && !editId){

            setSupplierId("");
            setDestinationWorkshopId("");
            setCurrency("USD");
            setItems([]);
            setPaymentType("cash");
            setCashlessPercent(0);

        }

    },[show,editId]);

    useEffect(() => {
        if (!show) {
            return;
        }
        (async () => {
            try {
                const r = await axios.get("/api/workshop-payroll/workshops", {
                    withCredentials: true,
                });
                setWorkshops(Array.isArray(r.data) ? r.data : []);
            } catch {
                setWorkshops([]);
            }
        })();
    }, [show]);

    useEffect(() => {
        if (!show) {
            return;
        }
        (async () => {
            try {
                const r = await axios.get("/api/materials");
                setMaterials(Array.isArray(r.data) ? r.data : []);
            } catch {
                /* залишаємо попередній список */
            }
        })();
    }, [show, editId]);

    useEffect(()=>{

        const copy = items.map(i=>{

            if(currency === "USD"){

                return {
                    ...i,
                    priceUAH: i.priceUSD * exchangeRate
                }

            }

            return {
                ...i,
                priceUSD: i.priceUAH / exchangeRate
            }

        });

        setItems(copy);

    },[exchangeRate]);

    useEffect(() => {

        setSupplierId("");

        setItems((prev) =>
            prev.map((item) => {

                if (category === "FABRIC") {

                    if (Array.isArray(item.rollAmounts) && item.rollAmounts.length > 0) {

                        const { qty: _drop, ...rest } = item;
                        return rest;

                    }

                    const rolls =
                        item.qty !== "" && item.qty != null && !Number.isNaN(Number(item.qty))
                            ? [String(item.qty)]
                            : [""];

                    const { qty: _drop, ...rest } = item;

                    return { ...rest, rollAmounts: rolls };

                }

                const rolls = item.rollAmounts;
                let q = item.qty;

                if (Array.isArray(rolls) && rolls.length > 0) {
                    q = rolls.reduce((s, x) => s + Number(x || 0), 0);
                }

                const { rollAmounts: _r, ...rest } = item;

                return {
                    ...rest,
                    qty: q === 0 || q === "" ? "" : q,
                };

            })
        );

    }, [category]);

    const loadPurchase = async()=>{

        const res = await axios.get(`/api/purchases/${editId}`);

        const {purchase,items} = res.data;

        const sup = purchase.supplierId;
        setSupplierId(
            sup && (sup._id || sup)
                ? String(sup._id || sup)
                : ""
        );
        setCurrency(purchase.currency);
        setExchangeRate(purchase.exchangeRate);
        setDate(purchase.date?.slice(0,10));
        setCategory(purchase.category);
        setPaymentType(purchase.paymentType || "cash");
        setCashlessPercent(
            purchase.cashlessPercent != null && purchase.cashlessPercent !== ""
                ? Number(purchase.cashlessPercent)
                : 0
        );

        const dw = purchase.destinationWorkshopId;
        setDestinationWorkshopId(
            dw ? String(dw._id != null ? dw._id : dw) : ""
        );

        const isFabricPurchase = purchase.category === "FABRIC";

        setItems(items.map(i=>{

            const base = {

                _id: i._id,
                typeId:i.materialId.typeId._id,
                typeName: i.materialId.typeId.name,
                color:i.materialId.color,
                unit:i.unit,
                priceUSD:i.priceUSD,
                priceUAH:i.priceUAH,
                comment: i.comment || "",

            };

            if (isFabricPurchase) {

                const rolls =
                    Array.isArray(i.rollAmounts) && i.rollAmounts.length > 0
                        ? i.rollAmounts.map(String)
                        : [String(i.qty)];

                return { ...base, rollAmounts: rolls };

            }

            if (i.bobbinCount != null && Number(i.bobbinCount) > 0) {
                return {
                    ...base,
                    qty: String(i.bobbinCount),
                };
            }

            return {
                ...base,
                qty: i.qty,
            };

        }));

    };

    useEffect(()=>{

        if(editId){

            loadPurchase();

        }

    },[editId])

    const loadData = async()=>{

        const s = await axios.get("/api/suppliers");
        const t = await axios.get("/api/material-types");
        const m = await axios.get("/api/materials");

        setSuppliers(s.data);
        setMaterialTypes(t.data);
        setMaterials(m.data);

    };

    const loadRates = async () => {

        try {

            const cached = localStorage.getItem("usdRate");
            const cachedTime = localStorage.getItem("usdRateTime");

            const now = Date.now();

            // если курс есть и ему меньше часа
            if (cached && cachedTime && now - cachedTime < 3600000) {

                setExchangeRate(Number(cached));
                return;

            }

            const res = await fetch("/api/exchange-rates");
            const data = await res.json();

            if(!Array.isArray(data)) return;

            const usd = data.find(
                c => c.currencyCodeA === 840 && c.currencyCodeB === 980
            );

            if (usd) {

                const rate = usd.rateSell || usd.rateCross;

                setExchangeRate(rate);

                localStorage.setItem("usdRate", rate);
                localStorage.setItem("usdRateTime", now);

            }

        } catch (e) {

            console.error("Failed to load exchange rates", e);

        }

    };

    const filteredSuppliers = suppliers.filter(s =>
        s.categories.includes(category)
    );

    const addItem = ()=>{

        const defaultColor = isPrintsEmbroideryCategory(category) ? MATERIAL_COLOR_NA_PLACEHOLDER : "";

        const row =
            category === "FABRIC"
                ? {
                    typeId: null,
                    typeName: "",
                    color: defaultColor,
                    unit:"",
                    rollAmounts: [""],
                    priceUSD:"",
                    priceUAH:"",
                    comment:"",
                }
                : {
                    typeId: null,
                    typeName: "",
                    color: defaultColor,
                    unit:"",
                    qty:"",
                    priceUSD:"",
                    priceUAH:"",
                    comment:"",
                };

        setItems([ ...items, row ]);

    };

    const fabricLineQty = (item) => {

        if (!Array.isArray(item.rollAmounts)) {
            return 0;
        }

        const raw = item.rollAmounts.reduce((s, x) => s + Number(x || 0), 0);
        return roundPurchaseItemQty(raw);

    };

    const updateRollAmount = (rowIndex, rollIndex, value) => {

        const copy = [...items];
        const rolls = [...copy[rowIndex].rollAmounts];

        rolls[rollIndex] = value;
        copy[rowIndex].rollAmounts = rolls;

        setItems(copy);

    };

    const addRollToRow = (rowIndex) => {

        const copy = [...items];

        copy[rowIndex].rollAmounts = [...copy[rowIndex].rollAmounts, ""];

        setItems(copy);

    };

    const removeRollFromRow = (rowIndex, rollIndex) => {

        const copy = [...items];
        const rolls = [...copy[rowIndex].rollAmounts];

        if (rolls.length <= 1) {
            return;
        }

        rolls.splice(rollIndex, 1);
        copy[rowIndex].rollAmounts = rolls;

        setItems(copy);

    };

    const updateItem = (index,field,value)=>{

        const copy = [...items];
        copy[index][field] = value;

        setItems(copy);

    };

    const handleColorChange = (index,color)=>{

        const copy = [...items];

        copy[index].color = color;

        const material = materials.find(
            (m) =>
                String(m.typeId?._id || m.typeId) === String(copy[index].typeId) &&
                m.color === color
        );

        if (material) {
            copy[index].priceUSD = material.priceUSD;
            copy[index].priceUAH = material.priceUAH;
            copy[index].unit = material.unit;
        }

        setItems(copy);

    };

    const handleUsdChange = (index,value)=>{

        const copy = [...items];

        copy[index].priceUSD = value;
        copy[index].priceUAH = value * exchangeRate;

        setItems(copy);

    };

    const handleUahChange = (index,value)=>{

        const copy = [...items];

        copy[index].priceUAH = value;
        copy[index].priceUSD = value / exchangeRate;

        setItems(copy);

    };

    const handleTypeInput = (index,value)=>{

        const copy = [...items];

        copy[index].typeName = value;

        const type = materialTypes.find(t =>
            t.name.toLowerCase() === value.trim().toLowerCase()
        );

        if(type){

            copy[index].typeId = type._id;

            if(type.units?.length === 1){
                copy[index].unit = type.units[0];
            }

        } else {
            copy[index].typeId = null;
        }

        if (category === "FABRIC" && !Array.isArray(copy[index].rollAmounts)) {
            copy[index].rollAmounts = [""];
        }

        setItems(copy);

    };

    const savePurchase = async()=>{

        const payload = {

            supplierId: supplierId && String(supplierId).trim() !== ""
                ? supplierId
                : null,
            currency,
            exchangeRate,
            category,
            date,
            paymentType,
            cashlessPercent,
            ...(destinationWorkshopId && String(destinationWorkshopId).trim() !== ""
                ? { destinationWorkshopId: String(destinationWorkshopId).trim() }
                : {}),
            items: items.map(i=>{

                const typeRow = materialTypes.find((t) => t._id === i.typeId);
                const matForUnit = findMaterialForPurchaseRow(i, materials);
                const usesBobbinQty =
                    category !== "FABRIC" &&
                    matForUnit &&
                    materialRowPurchaseIsBobbins(i, category, typeRow, materials);
                const resolvedUnit =
                    category === "FABRIC" && typeRow?.units?.length === 1
                        ? typeRow.units[0]
                        : usesBobbinQty
                          ? String(matForUnit.unit || i.unit || "м").trim() || "м"
                          : i.unit;

                const base = {

                    typeId:i.typeId,
                    typeName: i.typeName,
                    color:i.color,
                    unit: resolvedUnit,
                    comment: i.comment,
                    priceCurrency:
                        currency === "USD"
                            ? Number(i.priceUSD)
                            : Number(i.priceUAH)

                };

                if (category === "FABRIC") {
                    return {
                        ...base,
                        rollAmounts: i.rollAmounts.map((x) => Number(x)),
                    };
                }

                if (usesBobbinQty) {
                    return {
                        ...base,
                        bobbinCount: Number(i.qty),
                    };
                }

                return {
                    ...base,
                    qty: Number(i.qty),
                };

            })

        };

        if(editId){

            await axios.put(`/api/purchases/${editId}`,payload);

        }else{

            await axios.post("/api/purchases",payload);

        }

        onHide();
        reload();

    };

    const baseTotalUAH = items.reduce((sum, i) => {
        let lineQty = 0;
        if (category === "FABRIC") {
            lineQty = fabricLineQty(i);
        } else {
            /* Для рядка з намоткою поле qty — бобіни; ціна за бобіну. Інакше qty — од. рядка. */
            lineQty = Number(i.qty || 0);
        }
        const price = Number(i.priceUAH || 0);

        return sum + lineQty * price;
    }, 0);

    const totalUAH = purchaseUsesCashlessMarkup(paymentType)
        ? baseTotalUAH * (1 + Number(cashlessPercent || 0) / 100)
        : baseTotalUAH;

    const totalUAHPreview = roundMoneyUAH(totalUAH);

    const isValid = () => {

        if(items.length === 0) return false;

        for(const i of items){

            if(!i.typeId && !i.typeName) return false;
            if(!i.color) return false;

            const typeRow = materialTypes.find((t) => t._id === i.typeId);
            const unitStr = i.unit != null ? String(i.unit).trim() : "";
            const hasUnit =
                Boolean(unitStr) ||
                (category === "FABRIC" && typeRow?.units?.length === 1);

            if (!hasUnit) {
                return false;
            }

            if (category === "FABRIC") {

                if (!Array.isArray(i.rollAmounts) || i.rollAmounts.length === 0) {
                    return false;
                }

                for (const r of i.rollAmounts) {
                    if (r === "" || r == null || Number(r) <= 0) {
                        return false;
                    }
                }

            } else if (!i.qty || Number(i.qty) <= 0) {
                return false;
            }

            if(currency === "USD" && (!i.priceUSD || Number(i.priceUSD) <= 0)) return false;
            if(currency === "UAH" && (!i.priceUAH || Number(i.priceUAH) <= 0)) return false;

        }

        return true;
    };

    return(

        <Modal show={show} onHide={onHide} size="xl">

            <Modal.Header closeButton>
                <Modal.Title className="d-flex flex-column gap-1">
                    <span>{editId ? "Редагування закупки" : "Нова закупка"}</span>
                    <span className="text-muted fw-normal fs-6">
                        Закупівля у постачальника
                    </span>
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <p className="text-muted small mb-3">
                    Створюється документ закупівлі: постачальник, суми та (за наявності) облік боргу.
                    Рядки збільшують залишок на складі й оновлюють ціну на картці матеріалу. Якщо на картці матеріалу
                    заповнена властивість <strong>«Намотка»</strong> і од. складу — <strong>м</strong>, система трактує
                    рядок як <strong>закупівлю бобінами</strong>: навіть якщо в колонці «Од.» стоїть «шт» (типово так
                    вказують резинку), у «Кількість» вводьте <strong>число бобін</strong>; на склад зараховується{" "}
                    <strong>бобіни × намотка</strong> (метри). У ₴ / $ — <strong>ціна за одну бобіну</strong> (сума
                    рядка = ціна × бобіни). На картці зберігається <strong>ціна за метр</strong>; техкарти списують
                    метри. Якщо раніше закупили в «шт» без цього механізму — у «Матеріалах» за потреби увімкніть трактування
                    ціни як <strong>за бобіну</strong>.
                </p>
                <Form.Group className="mb-3">
                    <Form.Group className="mb-3">

                        <Form.Label>Дата закупки</Form.Label>

                        <Form.Control
                            type="date"
                            value={date}
                            onChange={(e)=>setDate(e.target.value)}
                        />

                    </Form.Group>

                    <Form.Label>Категорія матеріалів у цій закупці</Form.Label>

                    <Form.Select
                        value={category}
                        onChange={(e) => {
                            const newCat = e.target.value;
                            setCategory(newCat);
                            if (isPrintsEmbroideryCategory(newCat)) {
                                setItems((prev) => prev.map((it) => it.color ? it : { ...it, color: MATERIAL_COLOR_NA_PLACEHOLDER }));
                            }
                        }}
                    >
                        {MATERIAL_CATEGORY_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </Form.Select>

                </Form.Group>
                <Form.Group className="mb-3">

                    <Form.Label>Постачальник</Form.Label>

                    <Form.Select
                        value={supplierId}
                        onChange={(e)=>setSupplierId(e.target.value)}
                    >
                        <option value="">
                            Без постачальника (разова закупка)
                        </option>
                        {filteredSuppliers.length === 0 && (
                            <option disabled>
                                Немає постачальників у довіднику для цієї категорії
                            </option>
                        )}
                        {filteredSuppliers.map(s=>(
                            <option key={s._id} value={s._id}>
                                {s.name}
                            </option>
                        ))}

                    </Form.Select>

                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Склад зарахування</Form.Label>
                    <Form.Select
                        value={destinationWorkshopId}
                        onChange={(e) => setDestinationWorkshopId(e.target.value)}
                    >
                        <option value="">Наш цех (автоматично)</option>
                        {workshops.map((w) => (
                            <option key={w._id} value={w._id}>
                                {w.name}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <div className="d-flex gap-3 mb-3">

                    <Form.Group>

                        <Form.Label>Валюта</Form.Label>

                        <Form.Select
                            value={currency}
                            onChange={(e)=>setCurrency(e.target.value)}
                        >
                            <option value="USD">USD</option>
                            <option value="UAH">UAH</option>
                        </Form.Select>

                    </Form.Group>

                    <Form.Group>

                        <Form.Label>Курс</Form.Label>

                        <Form.Control
                            type="number"
                            value={exchangeRate}
                            onChange={(e)=>setExchangeRate(e.target.value)}
                        />

                    </Form.Group>
                    <div className="d-flex gap-3 mb-3">

                        <Form.Group>
                            <Form.Label>Форма оплати</Form.Label>
                            <Form.Select
                                value={paymentType}
                                onChange={(e)=>setPaymentType(e.target.value)}
                            >
                                <option value="cash">Готівка</option>
                                <option value="card">Картка</option>
                                <option value="fop_requisites">За реквізитами на ФОП</option>
                                {paymentType === "noncash" ? (
                                    <option value="noncash">Безготівка (старий запис)</option>
                                ) : null}
                            </Form.Select>
                        </Form.Group>

                        {purchaseUsesCashlessMarkup(paymentType) && (
                            <Form.Group>
                                <Form.Label>% націнки</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={cashlessPercent}
                                    onChange={(e)=>setCashlessPercent(e.target.value)}
                                />
                            </Form.Group>
                        )}

                    </div>

                </div>

                <Table bordered>

                    <thead>

                    <tr>

                        <th>Матеріал</th>
                        <th>Колір</th>
                        <th>Од.</th>
                        <th>{category === "FABRIC" ? "Рулони" : "Кількість"}</th>
                        <th>$</th>
                        <th>₴</th>
                        <th>Коментар</th>
                        <th></th>

                    </tr>

                    </thead>

                    <tbody>

                    {items.map((item,i)=>{

                        const type = materialTypes.find(t => t._id === item.typeId);
                        const matHint = findMaterialForPurchaseRow(item, materials);

                        return(

                            <tr key={i}>

                                <td>
                                    <Form.Control
                                        list={`types-${i}`}
                                        value={item.typeName || ""}
                                        onChange={(e)=>handleTypeInput(i,e.target.value)}
                                    />

                                    <datalist id={`types-${i}`}>

                                        {materialTypes
                                            .filter(t=>t.category === category)
                                            .map(t=>(
                                                <option key={t._id} value={t.name}/>
                                            ))}

                                    </datalist>

                                </td>

                                <td>

                                    <Form.Control
                                        list={`colors-${i}`}
                                        value={item.color}
                                        onChange={(e)=>handleColorChange(i,e.target.value)}
                                    />

                                    <datalist id={`colors-${i}`}>

                                        {type?.colors?.map(c=>(
                                            <option key={c} value={c}/>
                                        ))}

                                    </datalist>

                                </td>

                                <td>

                                    {category === "FABRIC" && type?.units?.length === 1 && (
                                        <span className="small">{type.units[0]}</span>
                                    )}

                                    {category === "FABRIC" && type?.units?.length > 1 && (
                                        <Form.Select
                                            size="sm"
                                            value={item.unit || ""}
                                            onChange={(e) => updateItem(i, "unit", e.target.value)}
                                        >
                                            <option value="">
                                                Оберіть од.
                                            </option>
                                            {type.units.map((u) => (
                                                <option key={u} value={u}>
                                                    {u}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    )}

                                    {category !== "FABRIC" &&
                                        materialRowPurchaseIsBobbins(item, category, type, materials) && (
                                            <Form.Control
                                                readOnly
                                                value="бобіна"
                                                tabIndex={-1}
                                                aria-label="Одиниця закупівлі — бобіна"
                                            />
                                        )}

                                    {(category !== "FABRIC" || !type?.units?.length) &&
                                        !(
                                            category !== "FABRIC" &&
                                            materialRowPurchaseIsBobbins(item, category, type, materials)
                                        ) && (
                                            <Form.Control
                                                size={category === "FABRIC" ? "sm" : undefined}
                                                value={item.unit}
                                                onChange={(e) => updateItem(i, "unit", e.target.value)}
                                                placeholder={category === "FABRIC" ? "кг / м …" : undefined}
                                            />
                                        )}

                                </td>

                                <td>

                                    {category === "FABRIC" ? (
                                        <div className="d-flex flex-column gap-1 align-items-stretch" style={{ minWidth: 210 }}>

                                            {(item.rollAmounts || [""]).map((r, ri) => (

                                                <div key={ri} className="d-flex align-items-center gap-1 flex-wrap">

                                                    <span className="text-muted small text-nowrap">
                                                        {ri + 1}.
                                                    </span>

                                                    <Form.Control
                                                        type="number"
                                                        size="sm"
                                                        step="any"
                                                        style={{ maxWidth: 110 }}
                                                        value={r}
                                                        onChange={(e) => updateRollAmount(i, ri, e.target.value)}
                                                    />

                                                    <span className="small text-nowrap text-muted">
                                                        {item.unit || "—"}
                                                    </span>

                                                    {(item.rollAmounts || []).length > 1 && (
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            className="py-0 px-1"
                                                            type="button"
                                                            onClick={() => removeRollFromRow(i, ri)}
                                                        >
                                                            ✕
                                                        </Button>
                                                    )}

                                                </div>

                                            ))}

                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                type="button"
                                                className="align-self-start"
                                                onClick={() => addRollToRow(i)}
                                            >
                                                + Рулон
                                            </Button>

                                            <div className="small text-muted">
                                                Разом:{" "}
                                                {fabricLineQty(item).toLocaleString("uk-UA", {
                                                    minimumFractionDigits: 0,
                                                    maximumFractionDigits: 4,
                                                })}{" "}
                                                {item.unit || ""}
                                            </div>

                                        </div>
                                    ) : (
                                        <Form.Control
                                            type="number"
                                            step="any"
                                            min={0}
                                            style={{ maxWidth: 130 }}
                                            value={item.qty}
                                            onChange={(e) => updateItem(i, "qty", e.target.value)}
                                        />
                                    )}

                                </td>

                                <td>

                                    <Form.Control
                                        type="number"
                                        value={item.priceUSD}
                                        onChange={(e)=>handleUsdChange(i,e.target.value)}
                                    />

                                </td>

                                <td>

                                    <Form.Control
                                        type="number"
                                        value={item.priceUAH}
                                        onChange={(e)=>handleUahChange(i,e.target.value)}
                                    />

                                    <PurchaseRowPriceHint material={matHint} />

                                </td>

                                <td>
                                    <Form.Control
                                        placeholder="Коментар..."
                                        value={item.comment || ""}

                                        onChange={(e) => {
                                            const val = e.target.value;

                                            const copy = [...items];
                                            copy[i].comment = val;

                                            setItems(copy);
                                        }}

                                        onBlur={async (e) => {
                                            const val = e.target.value;

                                            // 👉 если это редактирование — сразу в БД
                                            if (editId && item._id) {
                                                try {
                                                    await axios.put(`/api/purchase-items/${item._id}`, {
                                                        comment: val
                                                    });
                                                    console.log("saved");
                                                } catch (err) {
                                                    console.error(err);
                                                }
                                            }
                                        }}
                                    />
                                </td>

                                <td>

                                    <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={()=>setItems(items.filter((_,x)=>x!==i))}
                                    >
                                        ✕
                                    </Button>

                                </td>

                            </tr>

                        )

                    })}

                    </tbody>

                </Table>

                <Button variant="secondary" onClick={addItem}>
                    + Матеріал
                </Button>
                <div className="mt-4">

                    <div>
                        Сума: {roundMoneyUAH(baseTotalUAH).toLocaleString()} грн
                    </div>

                    {purchaseUsesCashlessMarkup(paymentType) && cashlessPercent !== 0 && (
                        <div style={{ color: "#888" }}>
                            +{cashlessPercent}% ={" "}
                            {roundMoneyUAH(totalUAH - baseTotalUAH).toLocaleString()} грн
                        </div>
                    )}

                    <strong>
                        Загальна сума (до копійок): {totalUAHPreview.toLocaleString()} грн
                    </strong>

                </div>

            </Modal.Body>

            <Modal.Footer>

                <Button onClick={savePurchase} disabled={!isValid()}>
                    Зберегти
                </Button>

            </Modal.Footer>

        </Modal>

    )

}